// Derives the GA4 reserved `value` (+ currency) for a tracked event so that
// conversions carry a monetary weight. Without this, GA4 reports eventValue=0
// and Google Ads (which imports GA4 conversions) optimizes blind on clicks
// instead of value. See docs/2026-06-02-ga4-conversion-value-design.md.
//
// IMPORTANT: these are expected-value weights for value-based bidding, NOT
// revenue. GA4 "Total revenue" becomes the sum of these estimates — read it
// as conversion value, not money earned.

export type Ga4ValueParams = Record<string, string | number | boolean | null | undefined>;

// Static fallback weights (USD), ordered by funnel strength:
// request > contact > click > search > view.
const STATIC_VALUE: Record<string, number> = {
  booking_request_submit: 80,
  booking_request_open: 50,
  prices_calculator_long_stay_contact: 40,
  contact_form_submit: 30,
  booking_cta_click: 15,
  booking_widget_click: 5,
  search_submit: 3,
  hero_search_submit: 3,
  apartment_view: 2,
};

// Events where a real monetary param, when present and positive, beats the
// static estimate (the user is already bound to a concrete quote/listing).
const REAL_VALUE_PARAM: Record<string, string> = {
  booking_request_submit: "total_usd",
  booking_request_open: "total_usd",
  apartment_view: "daily_rate_usd",
};

export function ga4ConversionValue(
  name: string,
  params: Ga4ValueParams = {},
): { value: number; currency: "USD" } | null {
  if (!(name in STATIC_VALUE)) return null;

  const realKey = REAL_VALUE_PARAM[name];
  if (realKey) {
    const raw = params[realKey];
    if (typeof raw === "number" && raw > 0) {
      return { value: raw, currency: "USD" };
    }
  }

  return { value: STATIC_VALUE[name], currency: "USD" };
}
